import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ErrorCode, type ApiErrorBody, type ControllingFigures, type FiguresPeriod } from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE, UNAUTHENTICATED_EVENT, api } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';

/** `GET /controlling/figures` and its CSV export (docs/dev/api-controlling.md, Controlling 04). */

export type FiguresScope = ControllingFigures['scope'];
export type { ControllingFigures, FiguresPeriod };

export const FIGURES_PERIODS: readonly FiguresPeriod[] = ['week', 'month', 'quarter'];
const SCOPES: readonly FiguresScope[] = ['me', 'team', 'organization'];

export function isFiguresPeriod(value: string | null): value is FiguresPeriod {
  return (FIGURES_PERIODS as readonly string[]).includes(value ?? '');
}

export function isFiguresScope(value: string | null): value is FiguresScope {
  return (SCOPES as readonly string[]).includes(value ?? '');
}

/** The query of both routes; without `scope` the server picks the widest available one. */
export function figuresQuery(period: FiguresPeriod, scope: FiguresScope | null): string {
  const search = new URLSearchParams({ period });
  if (scope) search.set('scope', scope);
  return search.toString();
}

export const figuresKey = (period: FiguresPeriod, scope: FiguresScope | null) =>
  [...queryRoots.controlling, 'figures', period, scope ?? 'default'] as const;

export function useFigures(period: FiguresPeriod, scope: FiguresScope | null) {
  return useQuery({
    queryKey: figuresKey(period, scope),
    queryFn: ({ signal }) =>
      api<ControllingFigures>(`/controlling/figures?${figuresQuery(period, scope)}`, { signal }),
    placeholderData: keepPreviousData,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 60_000,
  });
}

/** The API's error of a failed download (the JSON body of `lib/api.ts`). */
function errorOf(response: Response, json: unknown, path: string): ApiError {
  const body = (json as Partial<ApiErrorBody> | null)?.error ?? {
    code: ErrorCode.INTERNAL,
    message: GENERIC_ERROR_MESSAGE,
  };
  if (response.status === 401 && body.code === ErrorCode.UNAUTHENTICATED && !path.startsWith('/auth/')) {
    window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  }
  return new ApiError(response.status, body);
}

/** „Export": `GET /controlling/figures/export` with the page's query, saved as `kennzahlen.csv`. */
export function downloadFigures(period: FiguresPeriod, scope: FiguresScope | null): Promise<void> {
  return downloadCsv(`/controlling/figures/export?${figuresQuery(period, scope)}`, 'kennzahlen.csv');
}

/** Downloads a CSV export of the controlling module (the server's file name, else `fallbackName`). */
export async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`/api${path}`, {
    headers: { Accept: 'text/csv, application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const text = await response.text();
    let json: unknown = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    throw errorOf(response, json, path);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
